import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { Button, Divider, Form } from 'antd';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import Layout from './Layout';
import Question, {
    QUESTION_TYPE,
    QuestionCheckbox,
    QuestionRadio,
} from '../Question';
import { useState } from 'react';
import LastBtn from './LastBtn';

const SwitchFormItem = ({ form, options }) => {
    const questionComponents = {
        [QUESTION_TYPE.SELECT]: Question.Select,
        [QUESTION_TYPE.EDITOR]: Question,
        [QUESTION_TYPE.RADIO]: QuestionRadio,
        [QUESTION_TYPE.CHECKBOX]: QuestionCheckbox,
        [QUESTION_TYPE.DATE_TIME]: Question.DateTime,
        [QUESTION_TYPE.DATE]: Question.DateTime,
        [QUESTION_TYPE.SHORT]: Question.Short,
        [QUESTION_TYPE.LONG]: Question.Long,
        [QUESTION_TYPE.UPLOAD]: Question.Upload,
        [QUESTION_TYPE.TOPIC]: Question.TOPIC,
    };

    const QuestionComponent = questionComponents[form.type] || (() => <div />);
    const questionProps = { form, options, type: form.type };
    return <QuestionComponent {...questionProps} />;
};

// Validate props
SwitchFormItem.propTypes = {
    form: PropTypes.object.isRequired,
    options: PropTypes.array,
};

export default function Step({
    part,
    refId,
    style,
    hidden,
    onNextStep,
    isLastStep,
    justPreview,
    onPreviousStep,
    setIsSubmited,
    index: groupIndex = 0,
    currentStep = 1,
}) {
    const form = Form.useFormInstance();
    const groupQuestion = Form.useWatch('groupQuestion', form) || [];

    const [groupItem, setGroupItem] = useState([]);

    const previousStep = () => {
        onPreviousStep();
    };

    const nextStep = () => {
        if (isLastStep) return;

        const nameList = groupItem.map((_, i) => [
            'groupQuestion',
            groupIndex.toString(),
            i.toString(),
            'value',
        ]);

        const otherFields = groupItem
            .map((item, i) => ({
                item,
                index: i,
            }))
            .filter(
                ({ item }) =>
                    [QUESTION_TYPE.CHECKBOX, QUESTION_TYPE.RADIO].includes(
                        item.answerType
                    ) && item.value?.includes('Other')
            )
            .map(({ index }) => [
                'groupQuestion',
                groupIndex.toString(),
                index.toString(),
                'other',
            ]);

        const fieldsToValidate = [...nameList, ...otherFields];
        form.validateFields(fieldsToValidate)
            .then(onNextStep)
            .catch((error) => {
                form.scrollToField(error.errorFields[0].name, {
                    behavior: 'smooth',
                    block: 'center',
                });
            });
    };

    const filterQuestion = (groupIndex, index) => {
        if (groupQuestion.length === 0) return {};
        const group = groupQuestion[groupIndex][index];
        setGroupItem(groupQuestion[groupIndex]);
        return group;
    };

    const {
        mainColor: color = '#312966',
        textFormat: {
            description: {
                fontFamily: description_fontFamily = 'Kanit',
                fontSize: description_fontSize = '16px',
            } = {},
            partName: {
                fontFamily: partName_fontFamily = 'Kanit',
                fontSize: partName_fontSize = '16px',
            } = {},
            question: {
                fontFamily: question_fontFamily = 'Kanit',
                fontSize: question_fontSize = '16px',
            } = {},
        } = {},
    } = style || {};

    const styleFormItem = {
        description_fontFamily,
        description_fontSize,
        partName_fontFamily,
        partName_fontSize,
        question_fontFamily,
        question_fontSize,
    };

    return (
        <div hidden={hidden}>
            <Form.List name={groupIndex.toString()}>
                {() => (
                    <Layout>
                        <div className="p-6">
                            {part.partName && (
                                <strong
                                    className="text-base"
                                    style={{
                                        fontFamily: partName_fontFamily,
                                        fontSize: partName_fontSize,
                                    }}
                                >
                                    {parse(part.partName)}
                                </strong>
                            )}
                            {part.partDescription && (
                                <div
                                    style={{
                                        fontFamily: description_fontFamily,
                                        fontSize: description_fontSize,
                                    }}
                                >
                                    {parse(part.partDescription)}
                                </div>
                            )}
                            {part.groupQuestion.map((item, index) => {
                                return (
                                    <div key={item.id}>
                                        <SwitchFormItem
                                            form={{
                                                item: filterQuestion(
                                                    groupIndex,
                                                    index
                                                ),
                                                index,
                                                groupIndex,
                                                type: item.answerType,
                                                name: [
                                                    index.toString(),
                                                    'value',
                                                ],
                                                label: item.questionName,
                                                help:
                                                    item.questionDescription ||
                                                    null,
                                                rules: [
                                                    justPreview
                                                        ? null
                                                        : {
                                                              required:
                                                                  item.isRequire,
                                                              message:
                                                                  'กรุณากรอกข้อมูล',
                                                          },
                                                ],
                                                style: styleFormItem,
                                            }}
                                            options={item.answerValue?.map(
                                                ({ optionName }) => ({
                                                    label: optionName,
                                                    value: optionName,
                                                })
                                            )}
                                        />
                                        {[
                                            'questionName',
                                            'answerType',
                                            'storyId',
                                        ].map((field) => (
                                            <Form.Item
                                                key={field}
                                                initialValue={item[field]}
                                                name={[index, field]}
                                                noStyle
                                                hidden
                                            />
                                        ))}
                                    </div>
                                );
                            })}

                            {isLastStep ? (
                                <LastBtn
                                    refId={refId}
                                    setIsSubmited={setIsSubmited}
                                    currentStep={currentStep}
                                    justPreview={justPreview}
                                    part={part}
                                    previousStep={previousStep}
                                    color={color}
                                />
                            ) : (
                                <>
                                    <Divider />
                                    <div className="flex justify-between items-center">
                                        {currentStep !== 1 ? (
                                            <Button
                                                htmlType="button"
                                                onClick={previousStep}
                                                size="large"
                                                type="default"
                                                style={{
                                                    borderColor: color,
                                                    color,
                                                }}
                                            >
                                                <ArrowLeftOutlined />
                                                <span>Previous</span>
                                            </Button>
                                        ) : (
                                            <div />
                                        )}
                                        <Button
                                            htmlType="button"
                                            onClick={nextStep}
                                            size="large"
                                            type="primary"
                                            style={{
                                                backgroundColor: color,
                                                borderColor: color,
                                            }}
                                        >
                                            <span>Next</span>
                                            <ArrowRightOutlined />
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </Layout>
                )}
            </Form.List>
        </div>
    );
}

Step.propTypes = {
    part: PropTypes.shape({
        partName: PropTypes.string,
        partDescription: PropTypes.string,
        groupQuestion: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                answerType: PropTypes.string.isRequired,
                questionName: PropTypes.string.isRequired,
                questionDescription: PropTypes.string,
                isRequire: PropTypes.bool,
                answerValue: PropTypes.arrayOf(
                    PropTypes.shape({
                        optionName: PropTypes.string.isRequired,
                    })
                ),
                storyId: PropTypes.string,
            })
        ).isRequired,
    }).isRequired,
    refId: PropTypes.string,
    style: PropTypes.object,
    hidden: PropTypes.bool,
    onNextStep: PropTypes.func.isRequired,
    isLastStep: PropTypes.bool.isRequired,
    onPreviousStep: PropTypes.func.isRequired,
    currentStep: PropTypes.number.isRequired,
    index: PropTypes.number.isRequired,
    justPreview: PropTypes.bool,
    setIsSubmited: PropTypes.func.isRequired,
};
